import { useState, useEffect } from 'react';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    GoogleAuthProvider,
    signInWithPopup
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import Footer from '../components/Footer';


const PAGE_CONTENT = {
    "tech-deep-dive": {
        title: "The Scanning Engine",
        subtitle: "Technical architecture of our scanning core",
        sections: [
            {
                heading: "Distributed Execution",
                content: "Our engine utilizes a master-worker architecture where heavy DOM computation is offloaded to local scanning nodes. This allows for near-instant analysis of complex Single Page Applications (SPAs) without blocking the primary dashboard interface."
            },
            {
                heading: "Hybrid Rule Evaluation",
                content: "Our engine combines industry-standard axe-core rules with proprietary predictive models. We don't just check for static failures; we analyze dynamic interaction patterns to identify potential keyboard traps and ARIA state mismatches that standard scanners miss."
            },
            {
                heading: "Memory-Efficient Processing",
                content: "Large-scale crawls are processed using a streaming architecture. Page data is chunked and compressed via LZ-String before being transmitted to the cloud, ensuring high performance even on low-bandwidth connections."
            }
        ]
    },
    "security-deep-dive": {
        title: "Security Protocols",
        subtitle: "Zero-Knowledge architecture & data sovereignty",
        sections: [
            {
                heading: "End-to-End Encryption",
                content: "All sensitive data, including session recordings and cookies used for authenticated scans, are encrypted using AES-256-GCM. The keys are managed via the browser's crypto API and never leave your secure local context."
            },
            {
                heading: "Local Node Isolation",
                content: "When using the Desktop Agent, scans are executed in a sandboxed environment on your own hardware. This ensures that internal-only staging environments can be audited without opening firewall ports to external services."
            },
            {
                heading: "Compliance & Auditing",
                content: "Accessibility Scout maintains a 'Privacy by Default' stance. We do not store screenshots of your application, and all scan logs are automatically purged based on your account's retention policy (default 30 days)."
            }
        ]
    },
    "usages-deep-dive": {
        title: "Application Scenarios",
        subtitle: "How industry leaders leverage Accessibility Scout",
        sections: [
            {
                heading: "Continuous Integration",
                content: "Automate accessibility gates in your deployment pipeline. Use our CLI or Webhooks to trigger scans on every Pull Request, preventing regressions from ever reaching your production environment."
            },
            {
                heading: "Enterprise Compliance",
                content: "Generate formal PDF reports for legal and compliance teams. Track your 'Accessibility Score' across thousands of pages to demonstrate measurable progress in WCAG AA/AAA adherence."
            },
            {
                heading: "User Journey Validation",
                content: "Audit complex checkout flows, onboarding sequences, and interactive dashboards. Record the journey once with our extension, and automatically re-validate it whenever your UI changes."
            }
        ]
    },
    "documentation": {
        title: "Platform Documentation",
        subtitle: "Complete technical reference for Accessibility Scout integration",
        sections: [
            {
                heading: "Getting Started",
                content: "Accessibility Scout provides enterprise-grade accessibility auditing through a distributed network of scanning nodes. Our platform supports both manual single-page scans and automated multi-page crawls with full WCAG 2.1 Level AA compliance checking."
            },
            {
                heading: "Installation & Setup",
                content: "Deploy local scanning nodes via our Electron-based desktop agent. The agent operates silently in your system tray, listening for scan requests from our cloud infrastructure. All scan processing happens locally—your data never traverses external networks."
            },
            {
                heading: "Browser Extension",
                content: "Our companion Chrome/Edge extension enables user journey recording for authenticated application testing. Record complex multi-step workflows, capture session cookies securely, and replay them through your local scanning node for deep accessibility analysis."
            },
            {
                heading: "API Integration",
                content: "Integrate accessibility scanning into your CI/CD pipeline using our REST API. Generate API keys from your dashboard, configure webhook callbacks for real-time results, and automate compliance gates before deployment."
            },
            {
                heading: "WCAG Standards Support",
                content: "Our engine tests against 50+ WCAG 2.1 success criteria including: perceivable content (text alternatives, time-based media, adaptable layouts), operable interfaces (keyboard access, seizure safety, navigation), understandable content (readable text, predictable behavior), and robust markup (parsing, name/role/value compatibility)."
            }
        ]
    },
    "api-status": {
        title: "System Status",
        subtitle: "Real-time infrastructure health monitoring",
        sections: [
            {
                heading: "Current System Health",
                content: "All Accessibility Scout services are operational. Cloud infrastructure uptime: 99.97% over the past 30 days. No scheduled maintenance windows in the next 72 hours.",
                status: "operational"
            },
            {
                heading: "Distributed Node Network",
                content: "1,847 active scanning nodes across 12 geographic regions. Average node response time: 38ms. Load distribution is balanced with no regional hotspots detected.",
                status: "operational"
            },
            {
                heading: "API Gateway Performance",
                content: "REST API latency: P50 = 42ms, P95 = 128ms, P99 = 312ms. Rate limiting currently not triggered for any active customers. Webhook delivery success rate: 99.94%.",
                status: "operational"
            },
            {
                heading: "Scan Processing Pipeline",
                content: "Average scan completion time for single pages: 2.3 seconds. Multi-page crawls processing at 4.2 pages/second average throughput. No queue backlog present.",
                status: "operational"
            }
        ]
    },
    "nodes-grid": {
        title: "Node Infrastructure",
        subtitle: "Global distributed scanning architecture",
        sections: [
            {
                heading: "Architecture Overview",
                content: "Accessibility Scout operates on a hybrid architecture where scan execution is distributed to edge nodes closest to your infrastructure. This minimizes network latency and ensures scan data remains geographically proximate to your servers."
            },
            {
                heading: "Regional Deployments",
                content: "Primary clusters: US-East (Virginia, 312 nodes), US-West (Oregon, 287 nodes), EU-West (Dublin, 298 nodes), EU-Central (Frankfurt, 245 nodes), Asia-Pacific (Singapore, 312 nodes), Asia-South (Mumbai, 234 nodes). Additional presence in São Paulo, Sydney, Tokyo, and Toronto."
            },
            {
                heading: "Local Node Agents",
                content: "For maximum security and performance, deploy your own scanning nodes using our desktop agent. Local nodes receive scan requests via encrypted WebSocket connections, execute scans against target URLs, and return results without any page content leaving your network."
            },
            {
                heading: "Failover & Redundancy",
                content: "Automatic failover routes scan requests to healthy nodes within 500ms of node failure detection. Cross-region fallback ensures no single geographic incident impacts service availability. All nodes maintain encrypted heartbeat connections with 5-second intervals."
            }
        ]
    },
    "about": {
        title: "About Accessibility Scout",
        subtitle: "Our mission to make the web accessible for everyone",
        sections: [
            {
                heading: "Our Mission",
                content: "Over 1 billion people worldwide live with some form of disability. Despite this, 96% of websites fail basic accessibility standards. Accessibility Scout exists to close this gap—providing developers with real-time intelligence to create inclusive digital experiences."
            },
            {
                heading: "Why Accessibility Matters",
                content: "Web accessibility isn't just about compliance—it's about human dignity. When a visually impaired user can't navigate your checkout flow, or a motor-impaired person can't use your interface without a mouse, they're excluded from participating in the digital economy. Every inaccessible website is a door slammed shut."
            },
            {
                heading: "Our Approach",
                content: "Traditional accessibility auditing is slow, expensive, and reactive. Accessibility Scout transforms this with automated, continuous scanning integrated directly into development workflows. Catch violations before they reach production. Get AI-powered remediation suggestions. Track compliance trends over time."
            },
            {
                heading: "The Technology",
                content: "Our scanning engine, Synergy v1.1, extends beyond basic rule checking. We simulate screen reader navigation, analyze color contrast ratios dynamically, detect keyboard traps, validate ARIA implementations, and test against real assistive technology interaction patterns."
            },
            {
                heading: "Our Commitment",
                content: "Accessibility Scout is built accessibly. We practice what we preach—our platform meets WCAG 2.1 Level AA standards, with ongoing efforts toward Level AAA compliance. We transparent vulnerability reports on our own accessibility and maintain a public roadmap for improvements."
            }
        ]
    },
    "contact": {
        title: "Contact Us",
        subtitle: "We're here to help you build accessible experiences",
        sections: [
            {
                heading: "Technical Support",
                content: "For integration assistance, scanning issues, or technical questions: support@accessibilityscout.com. Our engineering team responds within 4 hours during business hours (9am-6pm EST, Mon-Fri). Critical infrastructure issues receive 24/7 response."
            },
            {
                heading: "Enterprise Sales",
                content: "For enterprise licensing, custom deployment options, or volume pricing: enterprise@accessibilityscout.com. We offer dedicated account management, SLA guarantees, and on-premise deployment options for regulated industries."
            },
            {
                heading: "Accessibility Consulting",
                content: "Need help beyond automated scanning? Our accessibility consultants provide manual expert audits, VPAT documentation, remediation guidance, and training programs. Contact consulting@accessibilityscout.com for custom engagements."
            },
            {
                heading: "Bug Bounty & Security",
                content: "Report security vulnerabilities responsibly to security@accessibilityscout.com. We operate a bug bounty program with rewards up to $5,000 for critical vulnerabilities. See our security page for disclosure guidelines."
            },
            {
                heading: "General Inquiries",
                content: "For partnership opportunities, media inquiries, or general questions: hello@accessibilityscout.com. Follow us on LinkedIn and Twitter for product updates and accessibility insights."
            }
        ]
    },
    "mascot": {
        title: "Meet Scout",
        subtitle: "Your AI accessibility companion",
        sections: [
            {
                heading: "Who is Scout?",
                content: "Scout is the AI intelligence powering our Remediation Assistant. More than a mascot, Scout represents our commitment to making accessibility guidance as human and approachable as possible."
            },
            {
                heading: "The Name",
                content: "Scout carries forward the legacy of accessible design into the modern development era, inspired by the foundational WCAG standards."
            },
            {
                heading: "How Scout Helps",
                content: "When you scan a page, Scout doesn't just report violations—it explains why they matter. What does a missing alt text mean for someone using a screen reader? How does poor color contrast affect users with low vision? Scout translates technical violations into human impact."
            },
            {
                heading: "AI-Powered Remediation",
                content: "Scout generates context-aware fix suggestions. Not generic 'add an alt attribute' advice, but specific recommendations based on your page structure, surrounding content, and likely user intent. Copy-paste ready code that actually solves the problem."
            },
            {
                heading: "Continuous Learning",
                content: "Scout improves with every scan. Anonymized patterns from millions of accessibility issues feed our machine learning models, producing increasingly accurate and helpful remediation guidance. Your feedback makes Scout smarter for everyone."
            }
        ]
    },
    "dmca": {
        title: "DMCA Policy",
        subtitle: "Intellectual property and content takedown procedures",
        sections: [
            {
                heading: "Our Commitment",
                content: "Accessibility Scout respects intellectual property rights and complies with the Digital Millennium Copyright Act (DMCA). We respond promptly to valid takedown requests for infringing content appearing in our scan results or documentation."
            },
            {
                heading: "What We Store",
                content: "Accessibility Scout stores scan results including page metadata, accessibility violations, and remediation suggestions. We do not store full page content, images, or copyrighted materials from scanned websites. Scan data is retained according to your account tier's data retention policy."
            },
            {
                heading: "Filing a Takedown Notice",
                content: "To file a DMCA takedown request, send a written notice to dmca@accessibilityscout.com including: identification of the copyrighted work, location of the infringing material in our system, your contact information, a statement of good faith belief, and your physical or electronic signature."
            },
            {
                heading: "Counter-Notification",
                content: "If you believe content was removed in error, you may file a counter-notification. Include: identification of the removed material, statement under penalty of perjury that removal was mistaken, your contact information, consent to federal court jurisdiction, and your signature."
            },
            {
                heading: "Repeat Infringers",
                content: "Accessibility Scout maintains a repeat infringer policy. Users who repeatedly upload or reference infringing content may have their accounts terminated. We reserve the right to disable access to any content reasonably believed to infringe third-party rights."
            }
        ]
    },
    "privacy": {
        title: "Privacy Policy",
        subtitle: "How we protect and handle your data",
        sections: [
            {
                heading: "Data Collection",
                content: "We collect: account information (email, name, organization), scan targets and results, usage analytics, and payment information for paid tiers. We do NOT collect: passwords (hashed only), full page content from scanned sites, or personal data from scanned pages."
            },
            {
                heading: "Data Security",
                content: "All data is encrypted in transit (TLS 1.3) and at rest (AES-256-GCM). Scan results from local nodes never transit our servers—they're transmitted directly to your browser via end-to-end encrypted channels. We maintain SOC 2 Type II compliance."
            },
            {
                heading: "Data Retention",
                content: "Free tier: 30-day scan history. Pro tier: 1-year scan history. Enterprise tier: configurable retention up to 7 years. You can export or delete your data at any time from account settings. Deleted data is purged from all systems within 30 days."
            },
            {
                heading: "Third-Party Sharing",
                content: "We never sell personal data. We share data only with: service providers (hosting, payment processing) under strict confidentiality agreements, and when required by law. Anonymous, aggregated accessibility statistics may be published for research purposes."
            },
            {
                heading: "Your Rights",
                content: "Under GDPR, CCPA, and similar regulations, you have rights to: access your data, correct inaccuracies, delete your account, export your data, and opt out of non-essential communications. Contact privacy@accessibilityscout.com to exercise these rights."
            },
            {
                heading: "Cookies & Tracking",
                content: "We use essential cookies for authentication and preferences. Optional analytics cookies help us improve the platform—you can disable these in your browser. We do not use advertising cookies or sell data to advertisers."
            }
        ]
    },
    "terms": {
        title: "Terms of Service",
        subtitle: "Agreement governing use of Accessibility Scout",
        sections: [
            {
                heading: "Acceptance",
                content: "By creating an account or using Accessibility Scout services, you agree to these Terms of Service. If you're using Accessibility Scout on behalf of an organization, you represent authority to bind that organization to these terms."
            },
            {
                heading: "Permitted Use",
                content: "Accessibility Scout is provided for legitimate accessibility testing of websites and applications you own or have explicit permission to test. You may not use our services to: probe systems without authorization, exploit discovered vulnerabilities, conduct denial-of-service testing, or violate any applicable laws."
            },
            {
                heading: "Account Responsibilities",
                content: "You're responsible for maintaining account security and all activity under your credentials. Notify us immediately of unauthorized access. Don't share API keys publicly or embed them in client-side code. We reserve the right to disable compromised accounts."
            },
            {
                heading: "Service Tiers & Billing",
                content: "Free tier provides limited scans per month with basic features. Paid tiers unlock additional scans, features, and support levels. Subscription fees are billed monthly or annually in advance. Refunds are provided only as required by applicable law."
            },
            {
                heading: "Intellectual Property",
                content: "Accessibility Scout and its components (scanning engine, reports, documentation, mascot) are our intellectual property. Scan results and remediation suggestions generated for your sites are yours to use freely. You grant us license to use anonymous, aggregated data to improve our services."
            },
            {
                heading: "Limitation of Liability",
                content: "Accessibility Scout is provided 'as is' without warranty. We don't guarantee detection of all accessibility issues or compliance with any specific standard. Our liability is limited to fees paid in the prior 12 months. We're not liable for indirect, consequential, or punitive damages."
            },
            {
                heading: "Termination",
                content: "You may close your account anytime. We may suspend or terminate accounts for Terms violations, non-payment, or at our discretion with 30 days notice. Upon termination, your data is deleted per our retention policy. Provisions that should survive termination (IP rights, limitation of liability) remain in effect."
            }
        ]
    }
};

function StaticPageView({ path, onBack }) {
    const data = PAGE_CONTENT[path] || { title: "Not Found", subtitle: "Page not available", sections: [{ heading: "Error", content: "The requested page could not be located." }] };
    const sections = data.sections || [];

    // Focus management for accessibility - focus main content on page load
    useEffect(() => {
        const mainHeading = document.getElementById('static-page-heading');
        if (mainHeading) {
            mainHeading.focus();
        }
    }, [path]);

    return (
        <main className="min-h-screen pt-40 pb-20 px-8 max-w-4xl mx-auto" role="main" aria-labelledby="static-page-heading">
            {/* Skip Link Target */}
            <div id="main-content" tabIndex="-1" className="sr-only">Main content starts here</div>

            {/* Article Content */}
            <article className="space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700">
                {/* Page Header */}
                <header className="space-y-4">
                    <h1
                        id="static-page-heading"
                        className="text-4xl md:text-5xl font-black uppercase tracking-tight"
                        tabIndex="-1"
                    >
                        {data.title}
                    </h1>
                    {data.subtitle && (
                        <p className="text-lg text-[var(--text-muted)] font-medium tracking-wide">
                            {data.subtitle}
                        </p>
                    )}
                    <div className="w-24 h-1 bg-[var(--accent)] shadow-[0.125em_0.125em_0em_var(--border)]" aria-hidden="true"></div>
                </header>

                {/* Content Sections */}
                <div className="space-y-10">
                    {sections.map((section, i) => (
                        <section key={i} className="space-y-4" aria-labelledby={`section-heading-${i}`}>
                            <div className="flex items-center gap-4">
                                {section.status && (
                                    <span
                                        className={`w-3 h-3 rounded-full ${section.status === 'operational' ? 'bg-[var(--success)]' : section.status === 'degraded' ? 'bg-[var(--warning)]' : 'bg-[var(--error)]'}`}
                                        aria-label={`Status: ${section.status}`}
                                        role="status"
                                    ></span>
                                )}
                                <h2
                                    id={`section-heading-${i}`}
                                    className="text-xl font-black uppercase tracking-widest text-[var(--text)]"
                                >
                                    {section.heading}
                                </h2>
                            </div>
                            <p className="text-base text-[var(--text-muted)] leading-relaxed pl-4 border-l-4 border-[var(--accent)]/30">
                                {section.content}
                            </p>
                        </section>
                    ))}
                </div>

                {/* Additional Info Card */}
                <aside className="mt-16 p-8 border-2 border-[var(--border)] bg-[var(--surface-elevated)] relative overflow-hidden" role="complementary" aria-label="Additional information">
                    <div className="absolute top-0 left-0 w-1 h-full bg-[var(--accent)]" aria-hidden="true"></div>
                    <div className="flex items-start gap-4">
                        <span className="text-2xl" aria-hidden="true">💡</span>
                        <div className="space-y-2">
                            <h3 className="text-sm font-black uppercase tracking-widest text-[var(--text)]">
                                Need More Information?
                            </h3>
                            <p className="text-sm text-[var(--text-muted)] leading-relaxed">
                                Our team is available to answer your questions. Visit our <a href="#contact" className="text-[var(--accent)] underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-[var(--accent)]">contact page</a> or reach out directly to our support team.
                            </p>
                        </div>
                    </div>
                </aside>
            </article>
        </main>
    );
}

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [currentPath, setCurrentPath] = useState('');
    const [liveAgentCount, setLiveAgentCount] = useState(null);
    const [statsRefreshInterval, setStatsRefreshInterval] = useState(300000);

    // Fetch live agent count from public stats endpoint
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch('https://us-central1-a11y-scanner-c052f.cloudfunctions.net/getPublicStats');
                if (response.ok) {
                    const data = await response.json();
                    setLiveAgentCount(data.liveAgentCount);
                    setStatsRefreshInterval(data.refreshIntervalMs || 300000);
                }
            } catch (err) {
                console.warn('Failed to fetch public stats:', err.message);
            }
        };
        fetchStats();
        const intervalId = setInterval(fetchStats, statsRefreshInterval);
        return () => clearInterval(intervalId);
    }, [statsRefreshInterval]);

    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.replace('#', '');
            if (['documentation', 'api-status', 'nodes-grid', 'about', 'contact', 'mascot', 'dmca', 'privacy', 'terms', 'tech-deep-dive', 'security-deep-dive', 'usages-deep-dive'].includes(hash)) {
                setCurrentPath(hash);
                window.scrollTo(0, 0);
            } else {
                setCurrentPath('');
            }
        };
        window.addEventListener('hashchange', handleHashChange);
        handleHashChange(); // Initial check
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setError('');
        if (isSignUp && !acceptedTerms) {
            setError('Account creation requires acceptance of Terms of Use.');
            return;
        }
        setLoading(true);
        try {
            if (isSignUp) {
                await createUserWithEmailAndPassword(auth, email, password);
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
        } catch (err) {
            setError(err.message.replace('Firebase: ', ''));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        setError('');
        if (isSignUp && !acceptedTerms) {
            setError('Account creation requires acceptance of Terms of Use.');
            return;
        }
        setLoading(true);
        try {
            const provider = new GoogleAuthProvider();
            await signInWithPopup(auth, provider);
        } catch (err) {
            setError(err.message.replace('Firebase: ', ''));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] font-sans overflow-x-hidden selection:bg-[var(--accent)] selection:text-black">
            {/* Skip Link for Keyboard Navigation */}
            <a href="#main-content" className="skip-link">
                Skip to main content
            </a>

            {/* Header / Nav */}
            <header className={`fixed top-0 inset-x-0 w-full z-[100] transition-all duration-500 ${scrolled ? 'bg-[var(--surface-elevated)] py-4' : 'bg-[var(--bg)] py-6'}`}>
                <div className={`absolute bottom-0 left-0 right-0 h-px ${scrolled ? 'bg-[var(--border)]' : 'bg-transparent'}`}></div>
                <nav className="max-w-7xl mx-auto px-8 flex items-center justify-between" aria-label="Main Navigation">
                    <div className="flex items-center gap-4">
                        <img src="/logo.png" alt="Accessibility Scout Logo" className="w-8 h-8 filter brightness-125" />
                        <h1 className="text-xl font-black uppercase tracking-[0.3em]  text-[#CC0000]">Accessibility Scout</h1>
                    </div>
                    <div className="hidden md:flex items-center gap-10 text-[0.625rem] font-black uppercase tracking-widest text-[var(--text-muted)] ">
                        <a href="#main" onClick={(e) => { e.preventDefault(); window.location.hash = 'main'; window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="hover:text-[#CC0000] transition-colors cursor-pointer">Home</a>
                        <a href="#main" onClick={(e) => { e.preventDefault(); window.location.hash = 'main'; setTimeout(() => document.getElementById('technology')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="hover:text-[#CC0000] transition-colors cursor-pointer">Technology</a>
                        <a href="#main" onClick={(e) => { e.preventDefault(); window.location.hash = 'main'; setTimeout(() => document.getElementById('security')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="hover:text-[#CC0000] transition-colors cursor-pointer">Security</a>
                        <a href="#main" onClick={(e) => { e.preventDefault(); window.location.hash = 'main'; setTimeout(() => document.getElementById('usages')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="hover:text-[#CC0000] transition-colors cursor-pointer">Usages</a>
                        {liveAgentCount !== null && (
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-none bg-[var(--success)]/10 border border-[var(--success)]/30">
                                <span className="w-2 h-2 rounded-full bg-[var(--success)] animate-pulse"></span>
                                <span className="text-[0.625rem] font-black uppercase tracking-[0.15em] text-[var(--success)]">
                                    {liveAgentCount} Global Node{liveAgentCount !== 1 ? 's' : ''} Online
                                </span>
                            </div>
                        )}
                        <button onClick={() => {
                            if (auth.currentUser) {
                                window.location.hash = '';
                            } else {
                                document.getElementById('auth-section').scrollIntoView({ behavior: 'smooth' });
                            }
                        }} className="bg-[var(--accent)] text-black px-6 py-2.5 rounded-none hover:scale-105 transition-all shadow-[0.25em_0.25em_0em_var(--border)]">
                            {auth.currentUser ? 'Launch Console' : 'Create Account'}
                        </button>
                    </div>
                </nav>
            </header>

            {currentPath ? <StaticPageView path={currentPath} onBack={() => { window.location.hash = "main"; }} /> : <main id="main-content" tabIndex="-1">

                {/* Hero Section */}
                <section className="relative min-h-[60vh] flex items-center pt-20 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-[var(--accent)]/5 via-transparent to-transparent pointer-events-none"></div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50em] h-[50em] bg-[var(--accent)]/5 blur-[7.5em] rounded-none pointer-events-none"></div>

                    <div className="max-w-7xl mx-auto px-8 grid lg:grid-cols-2 gap-6 items-center relative z-10 w-full">
                        <div className="space-y-3 animate-in fade-in slide-in-from-left-10 duration-1000">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-none bg-[var(--accent)]/10 border border-[var(--accent)]/20">
                                <span className="w-2 h-2 rounded-none bg-[var(--accent)] animate-pulse"></span>
                                <span className="text-[0.625rem] font-black uppercase tracking-[0.2em] text-[#CC0000]">New Version Released</span>
                            </div>
                            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9] italic">
                                Accessibility <br />
                                <span className="text-transparent stroke-text" style={{ WebkitTextStroke: '0.125em var(--accent)' }}>Without</span> <br />
                                Boundaries.
                            </h2>
                            <p className="max-w-md text-xs text-[var(--text-muted)] font-medium leading-relaxed uppercase tracking-widest">
                                Real-time WCAG 2.1 compliance audits powered by distributed node intelligence. Secure. Automated. Precise.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <button onClick={() => {
                                    if (auth.currentUser) {
                                        window.location.hash = '';
                                    } else {
                                        document.getElementById('auth-section').scrollIntoView({ behavior: 'smooth' });
                                    }
                                }} className="px-8 py-3.5 bg-[var(--accent)] text-black text-xs font-black uppercase tracking-[0.3em] rounded-none hover:scale-105 transition-all shadow-[0_1.25em_2.5em_-0.625em_var(--accent-glow)]">
                                    Get Started
                                </button>
                                <button onClick={() => window.location.hash = 'tech-deep-dive'} className="px-8 py-3.5 bg-transparent border-2 border-[var(--border)] text-xs font-black uppercase tracking-[0.3em] rounded-none hover:border-[var(--accent)] transition-all">
                                    How it Works
                                </button>
                            </div>
                        </div>

                        <div className="relative group perspective-1000 animate-in fade-in slide-in-from-right-10 duration-1000 delay-300 hidden lg:block">
                            <img
                                src="/mascot.png"
                                alt="Scout#030709 Mascot"
                                className="w-full max-w-[23.75em] mx-auto filter drop-shadow-[0.25em_0.25em_0em_var(--border)] group-hover:drop-shadow-[0.25em_0.25em_0em_var(--border)] transition-all duration-700 hover:rotate-2 hover:scale-105"
                            />
                            <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-[var(--surface-elevated)] border border-[var(--border)] p-4 rounded-none w-56 text-center space-y-1 group-hover: transition-all duration-500 translate-y-4 group-hover:translate-y-0 shadow-[0.25em_0.25em_0em_var(--border)]">
                                <p className="text-[0.5625rem] font-black text-[#CC0000] uppercase tracking-widest">System Message</p>
                                <p className="text-[0.5rem] font-bold uppercase tracking-widest leading-relaxed">
                                    "Hello! I am Scout. I'll be your accessibility advisor."
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Technology Section */}
                <section id="technology" className="py-20 border-t border-[var(--border)]">
                    <div className="max-w-7xl mx-auto px-8 space-y-12">
                        <div className="text-center space-y-4">
                            <h2 className="text-[0.625rem] font-black uppercase tracking-[0.5em] text-[#CC0000]">Advanced Core</h2>
                            <h3 className="text-3xl font-black uppercase tracking-[0.2em] italic">Proprietary Technology Stack</h3>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="neu-card p-8 space-y-4 hover:border-[var(--accent)] transition-all group cursor-pointer" onClick={() => window.location.hash = 'tech-deep-dive'}>
                                <span className="text-3xl block group-hover:scale-110 transition-transform">⚡</span>
                                <h4 className="text-xs font-black uppercase tracking-widest">Distributed Node Cluster</h4>
                                <p className="text-[0.625rem] font-medium leading-loose text-[var(--text-muted)] uppercase tracking-widest ">
                                    Offload intense scanning workloads to local nodes. Scans stay local, data stays secure, performance stays elite.
                                </p>
                            </div>
                            <div className="neu-card p-8 space-y-4 hover:border-[var(--accent)] transition-all group cursor-pointer" onClick={() => window.location.hash = 'usages-deep-dive'}>
                                <span className="text-3xl block group-hover:scale-110 transition-transform">🛣️</span>
                                <h4 className="text-xs font-black uppercase tracking-widest">User Journey Replays</h4>
                                <p className="text-[0.625rem] font-medium leading-loose text-[var(--text-muted)] uppercase tracking-widest ">
                                    record complex authenticated sessions with our companion extension. replay, analyze, and resolve roadblocks deep within your app.
                                </p>
                            </div>
                            <div className="neu-card p-8 space-y-4 hover:border-[var(--accent)] transition-all group cursor-pointer" onClick={() => window.location.hash = 'tech-deep-dive'}>
                                <span className="text-3xl block group-hover:scale-110 transition-transform">🤖</span>
                                <h4 className="text-xs font-black uppercase tracking-widest">AI Remediation Assistant</h4>
                                <p className="text-[0.625rem] font-medium leading-loose text-[var(--text-muted)] uppercase tracking-widest ">
                                    Scout processes raw violations into actionable remediation plans. Not just what's wrong, but how to fix it effectively.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Security Section */}
                <section id="security" className="py-20 bg-[var(--surface-elevated)]/30 border-y border-[var(--border)] relative overflow-hidden">
                    <div className="max-w-7xl mx-auto px-8 grid lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-6">
                            <h2 className="text-3xl font-black uppercase tracking-[0.2em] italic">Zero-Knowledge <br /> <span className="text-[#CC0000]">Security Architecture</span></h2>
                            <ul className="space-y-4">
                                <li className="flex gap-4">
                                    <span className="text-[#CC0000] text-lg" aria-hidden="true">🛡️</span>
                                    <div className="space-y-1">
                                        <h3 className="text-[0.625rem] font-black uppercase tracking-widest">End-to-End Encryption</h3>
                                        <p className="text-[0.5625rem] font-bold uppercase tracking-widest text-[var(--text-muted)] ">AES-GCM encryption for all session data. We can't see your passwords, and neither can anyone else.</p>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <span className="text-[#CC0000] text-lg" aria-hidden="true">🗝️</span>
                                    <div className="space-y-1">
                                        <h3 className="text-[0.625rem] font-black uppercase tracking-widest">Local Master Keys</h3>
                                        <p className="text-[0.5625rem] font-bold uppercase tracking-widest text-[var(--text-muted)] ">Your master key never leaves your machine. Full control over the decryption chain.</p>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div className="neu-card p-1 text-center bg-[var(--surface-elevated)] cursor-pointer" onClick={() => window.location.hash = 'security-deep-dive'}>
                            <div className="p-8 space-y-4 border-2 border-dashed border-[var(--border)] rounded-none">
                                <span className="text-4xl block animate-pulse">🔒</span>
                                <h3 className="text-xs font-black uppercase tracking-[0.3em]">Privacy First Engine</h3>
                                <button className="text-[0.5625rem] font-black uppercase tracking-[0.4em] bg-[var(--border)] px-4 py-2 rounded shadow-inner">DEEP DIVE →</button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Usages Section */}
                <section id="usages" className="py-20 border-b border-[var(--border)]">
                    <div className="max-w-7xl mx-auto px-8 space-y-12">
                        <div className="text-center space-y-4">
                            <h2 className="text-[0.625rem] font-black uppercase tracking-[0.5em] text-[#CC0000]">Ecosystem</h2>
                            <h3 className="text-3xl font-black uppercase tracking-[0.2em] italic">System Usages</h3>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[
                                { title: "E-Commerce", desc: "Ensure checkout flows and product grids are fully navigable via screen readers and keyboard.", icon: "🛒" },
                                { title: "Gov / Public", desc: "Maintain strict Section 508 and WCAG 2.1 AA compliance for public-facing portals.", icon: "🏛️" },
                                { title: "SaaS Tools", desc: "Audit interactive dashboards, complex tables, and dynamic data visualizations.", icon: "📊" },
                                { title: "Marketing", desc: "Optimize landing pages and forms for maximum conversion through inclusive design.", icon: "🎯" },
                                { title: "Education", desc: "Verify learning management systems (LMS) meet student inclusivity requirements.", icon: "🎓" },
                                { title: "Enterprise", desc: "Manage accessibility across massive internal intranets and employee tools.", icon: "🏢" }
                            ].map((usage, i) => (
                                <div key={i} className="bg-[var(--surface-elevated)] border-2 border-[var(--border)] p-6 space-y-3 hover:border-[var(--accent)] transition-all group">
                                    <span className="text-2xl group-hover:scale-125 transition-transform block">{usage.icon}</span>
                                    <h4 className="text-xs font-black uppercase tracking-widest">{usage.title}</h4>
                                    <p className="text-[0.5625rem] font-bold uppercase tracking-widest text-[var(--text-muted)] leading-loose">
                                        {usage.desc}
                                    </p>
                                    <button
                                        onClick={() => window.location.hash = 'usages-deep-dive'}
                                        className="text-[0.5rem] font-black text-[var(--accent)] uppercase tracking-widest hover:underline pt-2"
                                    >
                                        Case Study →
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Auth/Demo Section */}
                <section id="auth-section" className="py-20 flex items-center justify-center">
                    <div className="max-w-md w-full px-4">
                        <div className="text-center mb-8 space-y-3">
                            <h3 className="text-3xl font-black uppercase tracking-[0.2em]  italic">Account Access</h3>
                            <p className="text-[0.5625rem] font-black uppercase tracking-[0.3em] text-[#CC0000]">Sign in or create an account</p>
                        </div>

                        <form onSubmit={handleSubmit} className="neu-card p-8 space-y-5 border-[var(--border)] relative overflow-hidden group">
                            <div className="absolute top-0 left-0 right-0 h-[0.1875em] bg-gradient-to-r from-transparent via-[var(--accent)] to-transparent group-hover: transition-opacity"></div>

                            {error && (
                                <div className="p-4 bg-[var(--error)]/10 border border-[var(--error)]/30 text-[var(--error)] text-[0.5625rem] font-black text-center uppercase tracking-wider rounded-none">
                                    {error}
                                </div>
                            )}

                            <div className="space-y-3">
                                <label className="text-[0.5625rem] font-black text-[var(--text-muted)] uppercase tracking-widest block" htmlFor="email">Email Address</label>
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-5 py-4 neu-input text-xs placeholder-[var(--text-muted)]/50 font-black uppercase tracking-widest"
                                    placeholder="user@example.com"
                                />
                            </div>

                            <div className="space-y-3">
                                <label className="text-[0.5625rem] font-black text-[var(--text-muted)] uppercase tracking-widest block" htmlFor="password">Password</label>
                                <input
                                    id="password"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full px-5 py-4 neu-input text-xs font-black tracking-[0.5em]"
                                    placeholder="••••••••"
                                />
                            </div>

                            {isSignUp && (
                                <div className="flex items-start gap-4 p-4 bg-[var(--accent)]/5 border border-[var(--accent)]/20 rounded-none">
                                    <input
                                        type="checkbox"
                                        id="terms"
                                        checked={acceptedTerms}
                                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                                        className="mt-1 w-4 h-4 cursor-pointer accent-[var(--accent)]"
                                    />
                                    <label htmlFor="terms" className="text-[0.5625rem] font-black uppercase tracking-widest leading-loose ">
                                        I accept the Terms of Service and Privacy Policy.
                                    </label>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-[var(--accent)] text-black py-4 rounded-none text-xs font-black uppercase tracking-[0.4em] hover:scale-[1.02] active:scale-95 transition-all shadow-[0_0.625em_1.25em_rgba(var(--accent-rgb),0.2)] disabled:"
                            >
                                {loading ? 'Loading...' : isSignUp ? 'Create Account' : 'Sign In'}
                            </button>

                            <div className="relative py-4 flex items-center">
                                <div className="flex-grow border-t border-[var(--border)] "></div>
                                <span className="mx-6 text-[0.5rem] font-black uppercase ">or</span>
                                <div className="flex-grow border-t border-[var(--border)] "></div>
                            </div>

                            <button
                                type="button"
                                onClick={handleGoogleSignIn}
                                disabled={loading}
                                className="w-full py-4 border-2 border-[var(--border)] rounded-none text-[0.5625rem] font-black uppercase tracking-widest flex items-center justify-center gap-3 hover:border-[var(--accent)] transition-all bg-white/5"
                            >
                                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-4 h-4" alt="Google" />
                                Continue with Google
                            </button>

                            <p className="text-center text-[0.5625rem] font-black text-[var(--text-muted)] pt-4 uppercase tracking-[0.2em]">
                                {isSignUp ? 'Already have an account?' : 'New here?'}{' '}
                                <button
                                    type="button"
                                    onClick={() => setIsSignUp(!isSignUp)}
                                    className="text-[#CC0000] underline ml-1 "
                                >
                                    {isSignUp ? 'Login' : 'Register'}
                                </button>
                            </p>
                        </form>
                    </div>
                </section>
            </main>}

            {/* Footer */}
            <Footer />
        </div>
    );
}
